import { supabase } from "@/lib/supabase-client";

// Base type for all list items
interface BaseListItem {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  meta_data?: Record<string, any>;
  created_at: string;
}

// Specific types for each list table
export type Ability = BaseListItem;

export interface BillingItem extends BaseListItem {
  rate?: number;
}

export type DressCode = BaseListItem;

export type IncidentType = BaseListItem;

export interface Location extends BaseListItem {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  instructions?: string;
  market_id?: string;
}

export interface Market extends Omit<BaseListItem, "description"> {
  fips_code?: string;
}

export interface Position extends BaseListItem {
  billing_rate?: { rate: number; unit: string };
}

export type Restriction = BaseListItem;

export type Term = BaseListItem;

export const listService = {
  // ABILITIES
  async getAbilities(): Promise<Ability[]> {
    const { data, error } = await supabase
      .schema("lists")
      .from("abilities")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data as Ability[];
  },

  async getAbilityById(id: string): Promise<Ability> {
    const { data, error } = await supabase.schema("lists").from("abilities").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as Ability;
  },

  async addAbility(ability: Partial<Ability>): Promise<Ability> {
    const { data, error } = await supabase.schema("lists").from("abilities").insert([ability]).select().single();
    if (error) throw new Error(error.message);
    return data as Ability;
  },

  async updateAbility(id: string, patch: Partial<Ability>): Promise<Ability> {
    const { data, error } = await supabase
      .schema("lists")
      .from("abilities")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as Ability;
  },

  async deleteAbility(id: string): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("abilities").delete().eq("id", id);
    if (error) throw new Error(error.message);
    return true;
  },

  async deleteAbilitiesByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("abilities").delete().in("id", ids);
    if (error) throw new Error(error.message);
    return true;
  },

  // BILLING ITEMS
  async getBillingItems(): Promise<BillingItem[]> {
    const { data, error } = await supabase
      .schema("lists")
      .from("billing_items")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data as BillingItem[];
  },

  async getBillingItemById(id: string): Promise<BillingItem> {
    const { data, error } = await supabase.schema("lists").from("billing_items").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as BillingItem;
  },

  async addBillingItem(item: Partial<BillingItem>): Promise<BillingItem> {
    const { data, error } = await supabase.schema("lists").from("billing_items").insert([item]).select().single();
    if (error) throw new Error(error.message);
    return data as BillingItem;
  },

  async updateBillingItem(id: string, patch: Partial<BillingItem>): Promise<BillingItem> {
    const { data, error } = await supabase
      .schema("lists")
      .from("billing_items")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as BillingItem;
  },

  async deleteBillingItem(id: string): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("billing_items").delete().eq("id", id);
    if (error) throw new Error(error.message);
    return true;
  },

  async deleteBillingItemsByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("billing_items").delete().in("id", ids);
    if (error) throw new Error(error.message);
    return true;
  },

  // DRESS CODES
  async getDressCodes(): Promise<DressCode[]> {
    const { data, error } = await supabase
      .schema("lists")
      .from("dress_codes")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data as DressCode[];
  },

  async getDressCodeById(id: string): Promise<DressCode> {
    const { data, error } = await supabase.schema("lists").from("dress_codes").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as DressCode;
  },

  async addDressCode(dressCode: Partial<DressCode>): Promise<DressCode> {
    const { data, error } = await supabase.schema("lists").from("dress_codes").insert([dressCode]).select().single();
    if (error) throw new Error(error.message);
    return data as DressCode;
  },

  async updateDressCode(id: string, patch: Partial<DressCode>): Promise<DressCode> {
    const { data, error } = await supabase
      .schema("lists")
      .from("dress_codes")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as DressCode;
  },

  async deleteDressCode(id: string): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("dress_codes").delete().eq("id", id);
    if (error) throw new Error(error.message);
    return true;
  },

  async deleteDressCodesByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("dress_codes").delete().in("id", ids);
    if (error) throw new Error(error.message);
    return true;
  },

  // INCIDENT TYPES
  async getIncidentTypes(): Promise<IncidentType[]> {
    const { data, error } = await supabase
      .schema("lists")
      .from("incident_types")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data as IncidentType[];
  },

  async getIncidentTypeById(id: string): Promise<IncidentType> {
    const { data, error } = await supabase.schema("lists").from("incident_types").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as IncidentType;
  },

  async addIncidentType(incidentType: Partial<IncidentType>): Promise<IncidentType> {
    const { data, error } = await supabase
      .schema("lists")
      .from("incident_types")
      .insert([incidentType])
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as IncidentType;
  },

  async updateIncidentType(id: string, patch: Partial<IncidentType>): Promise<IncidentType> {
    const { data, error } = await supabase
      .schema("lists")
      .from("incident_types")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as IncidentType;
  },

  async deleteIncidentType(id: string): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("incident_types").delete().eq("id", id);
    if (error) throw new Error(error.message);
    return true;
  },

  async deleteIncidentTypesByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("incident_types").delete().in("id", ids);
    if (error) throw new Error(error.message);
    return true;
  },

  // LOCATIONS
  async getLocations(): Promise<Location[]> {
    const { data, error } = await supabase
      .schema("lists")
      .from("locations")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data as Location[];
  },

  async getLocationById(id: string): Promise<Location> {
    const { data, error } = await supabase.schema("lists").from("locations").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as Location;
  },

  async addLocation(location: Partial<Location>): Promise<Location> {
    const { data, error } = await supabase.schema("lists").from("locations").insert([location]).select().single();
    if (error) throw new Error(error.message);
    return data as Location;
  },

  async updateLocation(id: string, patch: Partial<Location>): Promise<Location> {
    const { data, error } = await supabase
      .schema("lists")
      .from("locations")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as Location;
  },

  async deleteLocation(id: string): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("locations").delete().eq("id", id);
    if (error) throw new Error(error.message);
    return true;
  },

  async deleteLocationsByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("locations").delete().in("id", ids);
    if (error) throw new Error(error.message);
    return true;
  },

  // MARKETS
  async getMarkets(): Promise<Market[]> {
    const { data, error } = await supabase
      .schema("lists")
      .from("markets")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data as Market[];
  },

  async getMarketById(id: string): Promise<Market> {
    const { data, error } = await supabase.schema("lists").from("markets").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as Market;
  },

  async addMarket(market: Partial<Market>): Promise<Market> {
    const { data, error } = await supabase.schema("lists").from("markets").insert([market]).select().single();
    if (error) throw new Error(error.message);
    return data as Market;
  },

  async updateMarket(id: string, patch: Partial<Market>): Promise<Market> {
    const { data, error } = await supabase.schema("lists").from("markets").update(patch).eq("id", id).select().single();
    if (error) throw new Error(error.message);
    return data as Market;
  },

  async deleteMarket(id: string): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("markets").delete().eq("id", id);
    if (error) throw new Error(error.message);
    return true;
  },

  async deleteMarketsByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("markets").delete().in("id", ids);
    if (error) throw new Error(error.message);
    return true;
  },

  // POSITIONS
  async getPositions(): Promise<Position[]> {
    const { data, error } = await supabase
      .schema("lists")
      .from("positions")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data as Position[];
  },

  async getPositionById(id: string): Promise<Position> {
    const { data, error } = await supabase.schema("lists").from("positions").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as Position;
  },

  async addPosition(position: Partial<Position>): Promise<Position> {
    const { data, error } = await supabase.schema("lists").from("positions").insert([position]).select().single();
    if (error) throw new Error(error.message);
    return data as Position;
  },

  async updatePosition(id: string, patch: Partial<Position>): Promise<Position> {
    const { data, error } = await supabase
      .schema("lists")
      .from("positions")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as Position;
  },

  async deletePosition(id: string): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("positions").delete().eq("id", id);
    if (error) throw new Error(error.message);
    return true;
  },

  async deletePositionsByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("positions").delete().in("id", ids);
    if (error) throw new Error(error.message);
    return true;
  },

  // RESTRICTIONS
  async getRestrictions(): Promise<Restriction[]> {
    const { data, error } = await supabase
      .schema("lists")
      .from("restrictions")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data as Restriction[];
  },

  async getRestrictionById(id: string): Promise<Restriction> {
    const { data, error } = await supabase.schema("lists").from("restrictions").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as Restriction;
  },

  async addRestriction(restriction: Partial<Restriction>): Promise<Restriction> {
    const { data, error } = await supabase.schema("lists").from("restrictions").insert([restriction]).select().single();
    if (error) throw new Error(error.message);
    return data as Restriction;
  },

  async updateRestriction(id: string, patch: Partial<Restriction>): Promise<Restriction> {
    const { data, error } = await supabase
      .schema("lists")
      .from("restrictions")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as Restriction;
  },

  async deleteRestriction(id: string): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("restrictions").delete().eq("id", id);
    if (error) throw new Error(error.message);
    return true;
  },

  async deleteRestrictionsByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("restrictions").delete().in("id", ids);
    if (error) throw new Error(error.message);
    return true;
  },

  // TERMS
  async getTerms(): Promise<Term[]> {
    const { data, error } = await supabase
      .schema("lists")
      .from("terms")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data as Term[];
  },

  async getTermById(id: string): Promise<Term> {
    const { data, error } = await supabase.schema("lists").from("terms").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as Term;
  },

  async addTerm(term: Partial<Term>): Promise<Term> {
    const { data, error } = await supabase.schema("lists").from("terms").insert([term]).select().single();
    if (error) throw new Error(error.message);
    return data as Term;
  },

  async updateTerm(id: string, patch: Partial<Term>): Promise<Term> {
    const { data, error } = await supabase.schema("lists").from("terms").update(patch).eq("id", id).select().single();
    if (error) throw new Error(error.message);
    return data as Term;
  },

  async deleteTerm(id: string): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("terms").delete().eq("id", id);
    if (error) throw new Error(error.message);
    return true;
  },

  async deleteTermsByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("terms").delete().in("id", ids);
    if (error) throw new Error(error.message);
    return true;
  },
};
